import { useMemo, useState, type ComponentType, type ReactNode, type SVGProps } from 'react';
import {
    Phone, PhoneCall, Calendar, MessageSquare, Clock, PhoneMissed,
    PhoneOff, RotateCcw, VolumeX, MessageCircle, Users, AlertTriangle,
} from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { VFStatCard } from '@/components/vf/StatCard';
import { Button } from '@/components/ui/button';
import { currentLocale, formatDurationWords, formatNumber, formatPercent } from '@/i18n/locale';
import { outcomeLabel } from '@/services/dialer/callOutcomeLabels';
import { useDrillCalls } from '@/hooks/queries/useDialerAnalytics';
import type { DialerCounters } from '@/api/endpoints/analytics';
import type { DrillMetric } from '@/lib/analyticsPeriod';
import { LoadError } from './LoadError';

/**
 * The KPI row and the secondary figures of the Performances page.
 *
 * Since W4 every figure comes from the server (POST /api/analytics/dialer/
 * performance) over the whole period: the page used to count the 500 newest
 * calls it had loaded, so a 6-month period silently counted a few days (T-d3).
 * « Connectés » is the metric contract (the call reached the agent), no
 * longer « outcome contacted or meeting ».
 *
 * Tiles: VFStatCard (loading, « Indisponible », trend only with a previous
 * period, sparkline). A tile opens the calls behind it (#86), read page by
 * page from the server with « Charger plus », never cut at 200.
 */

type IconType = ComponentType<SVGProps<SVGSVGElement> & { className?: string }>;

const rate = (num: number, denom: number) => (denom > 0 ? formatPercent((num / denom) * 100) : '—');

function Figure({ icon: Icon, label, value, sub }: { icon: IconType; label: string; value: ReactNode; sub?: string }) {
    return (
        <div className="flex items-start gap-2.5 min-w-0">
            <Icon className="w-4 h-4 text-muted-foreground/70 shrink-0 mt-0.5" />
            <div className="min-w-0">
                <p className="text-[11px] text-muted-foreground leading-tight truncate">{label}</p>
                <p className="text-lg font-semibold text-foreground font-data leading-snug truncate">{typeof value === 'number' ? formatNumber(value) : value}</p>
                {sub && <p className="text-[11px] text-muted-foreground/80 leading-tight truncate">{sub}</p>}
            </div>
        </div>
    );
}

function GroupTitle({ children }: { children: ReactNode }) {
    return <h3 className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground/70 mb-3">{children}</h3>;
}

export interface CallPerformanceMetricsProps {
    state: 'loading' | 'error' | 'ready';
    totals: DialerCounters | null;
    /** The comparison period, or the previous period of the same length (trends). Null: no trend. */
    previous: DialerCounters | null;
    /** Said in a banner when the user picked a comparison. */
    comparePeriodLabel?: string | null;
    series?: { total: number[]; connected: number[]; conversations: number[]; meetings: number[] } | null;
    conversationThreshold: number;
    periodDays: number | null;
    contactsConsumed?: number;
    contactsTotal?: number;
    /** State of the list-completion read behind « Contacts » (REV-W4-F10): never « 0 / 0 » while it loads or failed. */
    contactsState?: 'loading' | 'error' | 'ready';
    onRetryContacts?: () => void;
    /** The page-route filter of the calls behind a tile. */
    drillFilterFor: (metric: DrillMetric) => Record<string, unknown> | null;
    onRetry: () => void;
    error?: unknown;
}

export default function CallPerformanceMetrics({
    state, totals, previous, comparePeriodLabel = null, series = null, conversationThreshold, periodDays,
    contactsConsumed, contactsTotal, contactsState, onRetryContacts, drillFilterFor, onRetry, error,
}: CallPerformanceMetricsProps) {
    const { t } = useTranslation(['pages', 'dialer', 'common']);
    const [drill, setDrill] = useState<DrillMetric | null>(null);
    const s = totals;
    const toggle = (m: DrillMetric) => setDrill((d) => (d === m ? null : m));
    const metric = (key: 'total' | 'connected' | 'conversations' | 'meetings') => ({
        current: s ? s[key] : null,
        previous: previous ? previous[key] : null,
        kind: 'count' as const,
    });
    const hint = (sub: string) => (state === 'ready' && s ? sub : undefined);
    const callsPerDay = s && periodDays ? s.total / periodDays : null;
    const subs = s ? {
        total: callsPerDay !== null ? `${formatNumber(callsPerDay, { minimumFractionDigits: 1, maximumFractionDigits: 1 })} ${t('pages:analytics.callPerformance.hero.totalCalls.sub')}` : '',
        connected: `${rate(s.connected, s.total)} ${t('pages:analytics.callPerformance.hero.connected.sub')}`,
        conversations: `${rate(s.conversations, s.connected)} ${t('pages:analytics.callPerformance.hero.conversations.sub')}`,
        meetings: `${rate(s.meetings, s.connected)} ${t('pages:analytics.callPerformance.hero.meetings.sub')}`,
    } : null;

    const tiles: Array<{ id: DrillMetric; icon: IconType; label: string }> = [
        { id: 'total', icon: Phone, label: t('pages:analytics.callPerformance.hero.totalCalls.label') },
        { id: 'connected', icon: PhoneCall, label: t('pages:analytics.callPerformance.hero.connected.label') },
        { id: 'conversations', icon: MessageSquare, label: t('pages:analytics.callPerformance.hero.conversations.label', { threshold: conversationThreshold }) },
        { id: 'meetings', icon: Calendar, label: t('pages:analytics.callPerformance.hero.meetings.label') },
    ];

    return (
        <div className="space-y-4">
            {comparePeriodLabel && previous && state === 'ready' && (
                <p className="px-4 py-2.5 rounded-xl bg-[var(--vf-accent-bg)] border border-[var(--vf-accent-bd)] text-[var(--vf-accent-text)] text-sm">
                    {/* i18n-exempt: an HTML entity, not text */}
                    {t('pages:analytics.callPerformance.comparisonEnabled')}&nbsp;
                    <strong>{comparePeriodLabel}</strong>
                    {t('pages:analytics.callPerformance.comparisonDetail', {
                        total: previous.total,
                        plural: previous.total !== 1 ? 's' : '',
                        connected: previous.connected,
                        plural2: previous.connected !== 1 ? 's' : '',
                        meetings: previous.meetings,
                    })}
                </p>
            )}

            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4" data-testid="analytics-kpis">
                {tiles.map(({ id, icon: Icon, label }) => (
                    <div key={id} className="min-w-0">
                        <VFStatCard
                            label={label}
                            icon={<Icon className="w-4 h-4" />}
                            state={state}
                            metric={metric(id)}
                            series={series ? series[id] : undefined}
                            hint={hint(subs?.[id] ?? '')}
                            onClick={state === 'ready' ? () => toggle(id) : undefined}
                            className={drill === id ? 'ring-1 ring-[var(--vf-accent)] border-[var(--vf-accent)]' : ''}
                        />
                        {state === 'ready' && subs?.[id] && (
                            <p className="mt-1 px-1 text-xs text-muted-foreground truncate">{subs[id]}</p>
                        )}
                    </div>
                ))}
            </div>

            {drill && <DrillList metric={drill} filter={drillFilterFor(drill)} onClose={() => setDrill(null)} />}

            {state === 'error' ? (
                <LoadError error={error} onRetry={onRetry} className="bg-card rounded-xl border border-border p-5" />
            ) : (
                <div className="bg-card rounded-xl border border-border p-5 space-y-5" aria-busy={state === 'loading'}>
                    <div>
                        <GroupTitle>{t('pages:analytics.callPerformance.groups.outcomes')}</GroupTitle>
                        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-7 gap-x-4 gap-y-4">
                            {s ? (
                                <>
                                    <Figure icon={RotateCcw} label={t('pages:analytics.callPerformance.small.callbacks.label')} value={s.callbacks} sub={t('pages:analytics.callPerformance.small.pctOfCalls', { pct: rate(s.callbacks, s.total) })} />
                                    <Figure icon={PhoneCall} label={t('pages:analytics.callPerformance.small.callbacksConnected.label')} value={s.callbacksConnected} sub={t('pages:analytics.callPerformance.small.callbacksConnected.sub', { pct: rate(s.callbacksConnected, s.callbacks) })} />
                                    <Figure icon={PhoneOff} label={t('pages:analytics.callPerformance.small.notInterested.label')} value={s.notInterested} sub={t('pages:analytics.callPerformance.small.pctOfCalls', { pct: rate(s.notInterested, s.total) })} />
                                    <Figure icon={VolumeX} label={t('pages:analytics.callPerformance.small.voicemails.label')} value={s.voicemails} sub={t('pages:analytics.callPerformance.small.pctOfCalls', { pct: rate(s.voicemails, s.total) })} />
                                    <Figure icon={AlertTriangle} label={outcomeLabel('ios_filter', t)} value={s.iosFilters} sub={t('pages:analytics.callPerformance.small.pctOfCalls', { pct: rate(s.iosFilters, s.total) })} />
                                    <Figure icon={PhoneMissed} label={t('pages:analytics.callPerformance.small.cancelled.label')} value={s.cancelled} sub={t('pages:analytics.callPerformance.small.pctOfCalls', { pct: rate(s.cancelled, s.total) })} />
                                    <Figure icon={MessageCircle} label={t('pages:analytics.callPerformance.small.smsSent.label')} value={s.smsSent} sub={s.smsSent > 0 ? t('pages:analytics.callPerformance.small.pctOfCalls', { pct: rate(s.smsSent, s.total) }) : undefined} />
                                </>
                            ) : (
                                <div className="h-10 col-span-full animate-pulse motion-reduce:animate-none rounded bg-[var(--vf-surface-3)]" />
                            )}
                        </div>
                    </div>
                    <div className="border-t border-border pt-5">
                        <GroupTitle>{t('pages:analytics.callPerformance.groups.time')}</GroupTitle>
                        <div className="grid grid-cols-2 sm:grid-cols-3 gap-x-4 gap-y-4">
                            {s && (
                                <>
                                    <Figure icon={Clock} label={t('pages:analytics.callPerformance.small.totalTalkTime.label')} value={formatDurationWords(s.talkSeconds)} sub={t('pages:analytics.callPerformance.small.totalTalkTime.sub', { count: s.connected })} />
                                    <Figure icon={Clock} label={t('pages:analytics.callPerformance.small.avgTalkTime.label')} value={s.connected > 0 ? formatDurationWords(s.talkSeconds / s.connected) : '—'} sub={t('pages:analytics.callPerformance.small.avgTalkTime.sub')} />
                                </>
                            )}
                            {contactsState === 'loading' && (
                                <div data-testid="contacts-figure-loading" role="status" aria-label={t('pages:analytics.callPerformance.small.contacts.label')} className="h-10 animate-pulse motion-reduce:animate-none rounded bg-[var(--vf-surface-3)]" />
                            )}
                            {contactsState === 'error' && (
                                <div className="flex items-start gap-2.5 min-w-0" data-testid="contacts-figure-error">
                                    <Users className="w-4 h-4 text-muted-foreground/70 shrink-0 mt-0.5" />
                                    <div className="min-w-0">
                                        <p className="text-[11px] text-muted-foreground leading-tight truncate">{t('pages:analytics.callPerformance.small.contacts.label')}</p>
                                        <p className="text-lg font-semibold text-foreground leading-snug truncate">{t('common:stat.unavailable')}</p>
                                        {onRetryContacts && (
                                            <Button type="button" variant="link" size="sm" className="h-auto min-h-11 p-0 text-[11px]" onClick={onRetryContacts}>{t('common:actions.retry')}</Button>
                                        )}
                                    </div>
                                </div>
                            )}
                            {contactsState !== 'loading' && contactsState !== 'error' && contactsTotal !== undefined && (
                                <Figure
                                    icon={Users}
                                    label={t('pages:analytics.callPerformance.small.contacts.label')}
                                    value={contactsConsumed !== undefined ? `${formatNumber(contactsConsumed)} / ${formatNumber(contactsTotal)}` : t('common:states.noValue')}
                                    sub={contactsTotal > 0 ? t('pages:analytics.callPerformance.small.contacts.sub', { pct: rate(contactsConsumed ?? 0, contactsTotal) }) : undefined}
                                />
                            )}
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

/** The calls behind a tile, newest first, 50 at a time. */
function DrillList({ metric, filter, onClose }: { metric: DrillMetric; filter: Record<string, unknown> | null; onClose: () => void }) {
    const { t } = useTranslation(['pages', 'common', 'dialer']);
    const q = useDrillCalls(filter);
    const rows = useMemo(() => (q.data?.pages ?? []).flatMap((p) => p.items) as Array<Record<string, any>>, [q.data]);
    const titles: Record<DrillMetric, string> = {
        total: t('pages:analytics.callPerformance.drill.titleTotal'),
        connected: t('pages:analytics.callPerformance.drill.titleConnected'),
        conversations: t('pages:analytics.callPerformance.drill.titleConversations'),
        meetings: t('pages:analytics.callPerformance.drill.titleMeetings'),
    };
    return (
        <section className="rounded-xl border border-[var(--vf-accent)] bg-card overflow-hidden" aria-label={titles[metric]}>
            <div className="flex items-center justify-between px-4 py-2.5 border-b border-border bg-muted/30">
                <h3 className="text-sm font-medium text-foreground">{titles[metric]}</h3>
                <Button type="button" variant="ghost" size="sm" onClick={onClose}>{t('pages:analytics.callPerformance.drill.close')}</Button>
            </div>
            {q.isLoading ? (
                <div className="h-24 m-4 animate-pulse motion-reduce:animate-none rounded bg-[var(--vf-surface-3)]" />
            ) : q.isError ? (
                <LoadError error={q.error} onRetry={() => q.refetch()} className="px-4 py-6" />
            ) : rows.length === 0 ? (
                <p className="px-4 py-8 text-center text-sm text-muted-foreground">{t('pages:analytics.callPerformance.drill.empty')}</p>
            ) : (
                <div className="max-h-96 overflow-y-auto vf-scroll-visible">
                    <table className="w-full text-sm">
                        <thead>
                            <tr className="border-b border-border">
                                <th className="sticky top-0 z-10 bg-muted text-left px-4 py-2 text-xs font-medium text-muted-foreground">{t('pages:analytics.callPerformance.drill.columns.contact')}</th>
                                <th className="sticky top-0 z-10 bg-muted text-left px-4 py-2 text-xs font-medium text-muted-foreground">{t('pages:analytics.callPerformance.drill.columns.outcome')}</th>
                                <th className="sticky top-0 z-10 bg-muted text-left px-4 py-2 text-xs font-medium text-muted-foreground">{t('pages:analytics.callPerformance.drill.columns.duration')}</th>
                                <th className="sticky top-0 z-10 bg-muted text-left px-4 py-2 text-xs font-medium text-muted-foreground">{t('common:field.date')}</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border/60">
                            {rows.map((c) => {
                                const date = c.started_at || c.created_date;
                                return (
                                    <tr key={c.id}>
                                        <td className="px-4 py-2">
                                            <span className="text-foreground">{c.prospect_name || t('common:states.noValue')}</span>
                                            {c.prospect_company && <span className="text-xs text-muted-foreground"> · {c.prospect_company}</span>}
                                        </td>
                                        <td className="px-4 py-2 text-xs text-muted-foreground">
                                            {c.call_outcome ? outcomeLabel(c.call_outcome, t) : c.call_status ? outcomeLabel(c.call_status, t) : t('common:states.noValue')}
                                        </td>
                                        <td className="px-4 py-2 text-xs tabular-nums text-muted-foreground">{formatDurationWords(c.duration_seconds)}</td>
                                        <td className="px-4 py-2 text-xs tabular-nums text-muted-foreground">
                                            {date ? new Date(date).toLocaleString(currentLocale(), { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : t('common:states.noValue')}
                                        </td>
                                    </tr>
                                );
                            })}
                        </tbody>
                    </table>
                    {q.hasNextPage && (
                        <div className="p-3 text-center">
                            <Button type="button" variant="outline" size="sm" onClick={() => q.fetchNextPage()} disabled={q.isFetchingNextPage}>
                                {t('dialer:analytics.loadMore')}
                            </Button>
                        </div>
                    )}
                </div>
            )}
        </section>
    );
}
